import React, {
  useCallback, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { PropTypes } from 'prop-types';
import ScheduleGrid from './ScheduleGrid';
import ShiftBlock from './ShiftBlock';

import {
  onMouseDown as mouseDownHelper,
  onMouseUp as mouseUpHelper,
  onMouseMove as mouseMoveHelper,
  calculateWidths,
  getEditShiftFromBlockMouseDown,
} from './scheduleHelpers';

import { DAILY_LEFT_OFFSET } from './schedule.constants';

import Permissions from '../auth/Permissions';

export default function ScheduleDay({
  day,
  hideGrid,
  isWeek,
  rightDivider,
  onShiftCreate,
  newShift,
  onNewShiftChange,
  onShiftSelect,
  editShift,
  onShiftEdit,
  headerHeight,
  scrollTop = 0,
  shifts = [],
  containerY,
  hideHours,
  shiftBlockStyle,
  hideShiftText,
  viewType,
}) {
  const hasWritePerms = Permissions.has('SCHEDULE_WRITE');
  const containerRef = useRef(null);
  const [parentWidth, setParentWidth] = useState(0);
  const scale = useSelector((state) => state.schedule.scale);
  const settings = useSelector((state) => state.settings.company.settings);
  const hasRedesign = settings?.uxRedesignPt1;

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const updateWidth = () => setParentWidth(node.clientWidth);
    updateWidth();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isWeek, day]);

  const completedShifts = useMemo(() => {
    const filteredShifts = shifts
      .filter((shift) => !editShift || shift.id !== editShift.id)
      .sort((a, b) => (b.endTime - b.startTime) - (a.endTime - a.startTime));
    if (!parentWidth) return filteredShifts;

    return calculateWidths({
      dayShifts: filteredShifts,
      day,
      parentWidth,
      leftOffset: isWeek ? 0 : DAILY_LEFT_OFFSET,
    });
  }, [shifts, editShift, day, isWeek, parentWidth]);

  const onMouseDown = useCallback((e) => {
    mouseDownHelper({
      day,
      e,
      scrollTop,
      shifts: completedShifts,
      onShiftEdit,
      onNewShiftChange,
      isWeek,
      scale,
      headerHeight,
      containerY,
      hasRedesign,
      viewType,
    });
  }, [
    scrollTop,
    completedShifts,
    onShiftEdit,
    onNewShiftChange,
    isWeek,
    day,
    scale,
    headerHeight,
    containerY,
    hasRedesign,
    viewType,
  ]);

  const onMouseUp = useCallback((e) => {
    mouseUpHelper({
      day,
      e,
      shifts: completedShifts,
      scrollTop,
      editShift,
      onShiftEdit,
      newShift,
      onShiftSelect,
      onNewShiftChange,
      onShiftCreate,
      isWeek,
      scale,
      headerHeight,
      containerY,
      hasRedesign,
      viewType,
    });
  }, [
    scrollTop,
    newShift,
    onShiftCreate,
    completedShifts,
    editShift,
    onNewShiftChange,
    onShiftSelect,
    onShiftEdit,
    isWeek,
    day,
    scale,
    headerHeight,
    containerY,
    hasRedesign,
    viewType,
  ]);

  const onMouseMove = useCallback((e) => {
    mouseMoveHelper({
      day,
      e,
      scrollTop,
      editShift,
      newShift,
      onShiftEdit,
      onNewShiftChange,
      isWeek,
      scale,
      headerHeight,
      containerY,
      hasRedesign,
      viewType,
    });
  }, [
    scrollTop,
    newShift,
    editShift,
    onNewShiftChange,
    onShiftEdit,
    isWeek,
    day,
    scale,
    headerHeight,
    containerY,
    hasRedesign,
    viewType,
  ]);

  const hasEdit = editShift && editShift.day && editShift.day.hasSame(day, 'day');
  const hasNew = newShift && newShift.day && newShift.day.hasSame(day, 'day');
  return (
    <div
      ref={containerRef}
      className={rightDivider
        ? 'schedule-view-inner-container-right-divider'
        : 'schedule-view-inner-container'}
      onMouseDown={hasWritePerms ? onMouseDown : null}
      onMouseMove={hasWritePerms ? onMouseMove : null}
      onMouseUp={hasWritePerms ? onMouseUp : null}
      role="button"
      tabIndex={-1}
    >
      <ScheduleGrid hideGrid={hideGrid} hideHours={hideHours}/>
      {
        completedShifts.map((shift) => (
          <ShiftBlock
            key={shift.id}
            isWeek={isWeek}
            shift={shift}
            onMouseDown={(e) => {
              e.stopPropagation();
              onShiftEdit(getEditShiftFromBlockMouseDown({ e, shift, scale }));
            }}
            onClick={(e) => {
              e.stopPropagation();
              onShiftSelect(shift);
            }}
            style={{ cursor: 'pointer', ...shiftBlockStyle }}
            hideShiftText={hideShiftText}
          />
        ))
      }
      {
        hasEdit
        && (
        <ShiftBlock
          key={editShift.id}
          isWeek={isWeek}
          shift={editShift}
          style={{
            cursor: 'move',
            zIndex: 100,
            left: isWeek ? 0 : DAILY_LEFT_OFFSET,
            width: '100%',
            ...shiftBlockStyle,
          }}
          hideShiftText={hideShiftText}
        />
        )
      }
      {hasNew
        && (
        <ShiftBlock
          isWeek={isWeek}
          shift={newShift}
          style={{
            cursor: 'row-resize',
            zIndex: 100,
            ...newShift.complete ? {} : { maxWidth: '100%' },
            ...shiftBlockStyle
          }}
          hideShiftText={hideShiftText}
        />
        )}
    </div>
  );
}

ScheduleDay.propTypes = {
  day: PropTypes.instanceOf(DateTime).isRequired,
  hideGrid: PropTypes.bool,
  isWeek: PropTypes.bool,
  rightDivider: PropTypes.bool,
  onNewShiftChange: PropTypes.func.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  onShiftCreate: PropTypes.func.isRequired,
  onShiftEdit: PropTypes.func.isRequired,
  newShift: PropTypes.shape({
    complete: PropTypes.bool,
    day: PropTypes.instanceOf(DateTime),
  }),
  editShift: PropTypes.shape({
    id: PropTypes.string,
    day: PropTypes.instanceOf(DateTime),
  }),
  headerHeight: PropTypes.number.isRequired,
  scrollTop: PropTypes.number,
  shifts: PropTypes.arrayOf(PropTypes.shape({})),
  containerY: PropTypes.number.isRequired,
  hideHours: PropTypes.bool,
  shiftBlockStyle: PropTypes.shape({}),
};

ScheduleDay.defaultProps = {
  hideGrid: false,
  isWeek: false,
  rightDivider: false,
  newShift: {},
  editShift: {},
  scrollTop: 0,
  shifts: [],
  hideHours: false,
  shiftBlockStyle: {},
};
